import React, { useEffect, useMemo, useState } from 'react';
import { getPlatformBaseUrl } from '../../constants/auth';
import { authService } from '../../services/auth';
import { i18nService } from '../../services/i18n';
import { localStore } from '../../services/store';
import { CONFIG_KEYS } from '../../config';
import type { AuthState } from '../../types/auth';
import { orgBrandingService } from '../../services/orgBranding';
import BoclawLogo from '../icons/BoclawLogo';
import { LightBackground } from './LegacyAuthBackground';

/**
 * OAuth 授权页面 —— 供 BoCoder Desktop 客户端通过浏览器登录时使用。
 *
 * URL: /oauth/authorize?redirect_uri=http://127.0.0.1:{port}/callback&client_id=boclaw-desktop
 *
 * 流程:
 *  1. 如果 Web 端已登录 → 显示「授权确认」→ 用户点击授权 → 重定向回 redirect_uri 并携带 token
 *  2. 如果 Web 端未登录 → 显示登录表单 → 登录成功后自动重定向回 redirect_uri 并携带 token
 */

type OAuthPhase = 'checking' | 'login' | 'authorize' | 'redirecting' | 'error';

const OAuthAuthorize: React.FC = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const redirectUri = params.get('redirect_uri') || '';
  const clientId = params.get('client_id') || '';

  const [phase, setPhase] = useState<OAuthPhase>('checking');
  const [error, setError] = useState<string | null>(null);

  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaData, setCaptchaData] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorTicket, setTwoFactorTicket] = useState<string | null>(null);
  const [pending2FA, setPending2FA] = useState<{ username: string; password: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orgBranding, setOrgBranding] = useState(orgBrandingService.getBranding());

  useEffect(() => {
    // OAuth 页面独立渲染，不走 App.tsx 主初始化流程，需自行初始化 orgBrandingService
    void orgBrandingService.init().then(() => {
      setOrgBranding(orgBrandingService.getBranding());
    });
    return orgBrandingService.subscribe(() => {
      setOrgBranding(orgBrandingService.getBranding());
    });
  }, []);

  // Validate redirect_uri
  const isValidRedirect = useMemo(() => {
    if (!redirectUri) return false;
    try {
      const url = new URL(redirectUri);
      // 只允许回调到本地地址
      return url.hostname === '127.0.0.1' || url.hostname === 'localhost';
    } catch {
      return false;
    }
  }, [redirectUri]);

  // 验证 token 是否有效（调用 Platform API）
  const validateToken = async (token: string): Promise<boolean> => {
    try {
      const baseUrl = getPlatformBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  // Check initial auth state
  useEffect(() => {
    if (!isValidRedirect) {
      setError(i18nService.t('oauthInvalidRedirect'));
      setPhase('error');
      return;
    }

    const checkAuth = async () => {
      // 优先检查 boclaw_token
      let token = localStorage.getItem('boclaw_token');

      // 兜底：从 web 端 auth 缓存中恢复 token
      if (!token) {
        try {
          const cached = localStorage.getItem('boclaw_auth_state_cache');
          if (cached) {
            const parsed = JSON.parse(cached);
            token = parsed?.token || null;
            // 恢复到 boclaw_token 以便后续流程使用
            if (token) {
              localStorage.setItem('boclaw_token', token);
            }
          }
        } catch { /* ignore parse error */ }
      }

      if (token) {
        // 验证 token 是否仍然有效（可能已被吊销）
        const valid = await validateToken(token);
        if (valid) {
          setPhase('authorize');
          return;
        }
        // token 无效，清除旧 token
        localStorage.removeItem('boclaw_token');
      }

      // 尝试通过 authService 恢复登录状态（会自动从 localStore/缓存中恢复并刷新）
      try {
        const restored = await authService.init();
        if (restored?.token) {
          const restoredValid = await validateToken(restored.token);
          if (restoredValid) {
            localStorage.setItem('boclaw_token', restored.token);
            setPhase('authorize');
            return;
          }
        }
      } catch { /* authService 恢复失败，继续进入登录 */ }

      setPhase('login');
      void loadCaptcha();
    };

    void checkAuth();
  }, [isValidRedirect]);

  const loadCaptcha = async () => {
    try {
      const result = await authService.fetchCaptcha();
      if (result) {
        setCaptchaId(result.captchaId);
        setCaptchaData(result.captchaData);
        setCaptchaCode('');
      }
    } catch {
      setCaptchaId('');
      setCaptchaData('');
    }
  };

  // 直接调用 Platform API 进行登录，以获取完整的 token 信息（包括 refresh_token）
  const loginAndGetTokens = async (body: Record<string, string>): Promise<{
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
  }> => {
    const baseUrl = getPlatformBaseUrl();
    const response = await fetch(`${baseUrl}/api/v1/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const payload = await response.json();
    const data = payload.data ?? payload;

    if (data.require2fa || data.requires2fa || data.two_factor_required) {
      throw Object.assign(new Error('2FA_REQUIRED'), {
        ticket: data.ticket || data.session || null,
      });
    }

    if (!response.ok || payload.success === false) {
      throw new Error(payload.message || payload.msg || payload.error || 'Login failed');
    }

    const token = data.token || data.access_token;
    if (!token) {
      throw new Error('No token in response');
    }

    return {
      access_token: token,
      refresh_token: data.refresh_token,
      expires_in: data.expires_in,
    };
  };

  const loginWith2FAAndGetTokens = async (body: Record<string, string>): Promise<{
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
  }> => {
    const baseUrl = getPlatformBaseUrl();
    const response = await fetch(`${baseUrl}/api/v1/auth/login/2fa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const payload = await response.json();
    const data = payload.data ?? payload;

    if (!response.ok || payload.success === false) {
      throw new Error(payload.message || payload.msg || payload.error || '2FA verification failed');
    }

    const token = data.token || data.access_token;
    if (!token) {
      throw new Error('No token in response');
    }

    return {
      access_token: token,
      refresh_token: data.refresh_token,
      expires_in: data.expires_in,
    };
  };

  // 用已获取的 token 直接设置 web 端登录状态（避免重复发 login 请求）
  const persistWebLogin = async (token: string) => {
    try {
      const baseUrl = getPlatformBaseUrl();
      const resp = await fetch(`${baseUrl}/api/v1/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('fetch profile failed');
      const payload = await resp.json();
      const raw = payload.data ?? payload;
      const user = {
        id: String(raw.id ?? ''),
        username: raw.username ?? '',
        displayName: raw.display_name || raw.username || '',
        email: raw.email ?? null,
        role: raw.role ?? null,
        status: raw.status ?? null,
      };
      const auth: AuthState = {
        token,
        authMethod: 'bearer',
        user,
        userIdHeader: user.id,
        loggedInAt: Date.now(),
      };
      localStorage.setItem('boclaw_token', token);
      localStorage.setItem('boclaw_auth_state_cache', JSON.stringify(auth));
      await localStore.setItem(CONFIG_KEYS.AUTH, auth);
    } catch {
      localStorage.setItem('boclaw_token', token);
    }
  };

  const completeRedirect = async (tokens: {
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
  }) => {
    const url = new URL(redirectUri);
    url.searchParams.set('access_token', tokens.access_token);
    if (tokens.refresh_token) {
      url.searchParams.set('refresh_token', tokens.refresh_token);
    }
    if (tokens.expires_in) {
      url.searchParams.set('expires_in', String(tokens.expires_in));
    }

    // 后台通知 Desktop 客户端接收 token，页面保留在授权页显示成功状态
    try {
      await fetch(url.toString(), { mode: 'no-cors' });
    } catch {
      // Desktop 回调可能不支持 fetch，兜底用 img beacon
      const img = new Image();
      img.src = url.toString();
    }

    setPhase('redirecting');
  };

  // 用已有 token 向 Platform 申请一个独立的新 token 给桌面端
  const generateClientToken = async (existingToken: string): Promise<{
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
  } | null> => {
    try {
      const baseUrl = getPlatformBaseUrl();
      const response = await fetch(`${baseUrl}/api/v1/auth/oauth/token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${existingToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: clientId || 'boclaw-desktop',
          grant_type: 'token_exchange',
        }),
      });

      if (!response.ok) return null;

      const payload = await response.json();
      const data = payload.data ?? payload;
      const token = data.access_token || data.token;
      if (!token) return null;

      return {
        access_token: token,
        refresh_token: data.refresh_token,
        expires_in: data.expires_in,
      };
    } catch {
      return null;
    }
  };

  // 已登录 → 签发新 token 给桌面端（不共享 web 端的 token）
  const handleAuthorize = async () => {
    const savedToken = localStorage.getItem('boclaw_token');
    if (!savedToken) {
      setPhase('login');
      void loadCaptcha();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const newTokens = await generateClientToken(savedToken);
      if (newTokens) {
        completeRedirect(newTokens);
        return;
      }
      completeRedirect({ access_token: savedToken });
    } catch {
      completeRedirect({ access_token: savedToken });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 未登录 → 登录表单提交
  const handleLoginSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      if (pending2FA) {
        if (!twoFactorCode.trim()) {
          throw new Error(i18nService.t('authTwoFactorCodeRequired'));
        }
        const body: Record<string, string> = {
          username: pending2FA.username,
          password: pending2FA.password,
          code: twoFactorCode.trim(),
        };
        if (twoFactorTicket) {
          body.ticket = twoFactorTicket;
        }
        const tokens = await loginWith2FAAndGetTokens(body);
        await persistWebLogin(tokens.access_token);
        const clientTokens = await generateClientToken(tokens.access_token);
        completeRedirect(clientTokens || tokens);
        return;
      }

      if (!username.trim() || !password) {
        throw new Error(i18nService.t('authUsernamePasswordRequired'));
      }

      const body: Record<string, string> = {
        username: username.trim(),
        password,
      };
      if (captchaId && captchaCode) {
        body.captcha_id = captchaId;
        body.captcha_code = captchaCode.trim();
      }

      const tokens = await loginAndGetTokens(body);
      await persistWebLogin(tokens.access_token);
      const clientTokens = await generateClientToken(tokens.access_token);
      completeRedirect(clientTokens || tokens);
    } catch (submitError: any) {
      if (submitError.message === '2FA_REQUIRED') {
        setPending2FA({ username: username.trim(), password });
        setTwoFactorTicket(submitError.ticket || null);
        setError(null);
      } else {
        setError(submitError.message || i18nService.t('connectionFailed'));
        void loadCaptcha();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting) {
      void handleLoginSubmit();
    }
  };

  const orgCode = orgBranding?.code || 'BoCoder';

  const title = useMemo(() => {
    if (phase === 'authorize') return i18nService.t('oauthAuthorizeTitle').replace('{orgCode}', orgCode);
    if (pending2FA) return i18nService.t('authTwoFactorTitle');
    return orgCode;
  }, [phase, pending2FA, orgCode]);

  const subtitle = useMemo(() => {
    if (phase === 'authorize') return i18nService.t('oauthAuthorizeDesc').replace('{orgCode}', orgCode);
    if (pending2FA) return null;
    return orgBranding?.name || 'AI 智能助手';
  }, [phase, pending2FA, orgBranding, orgCode]);

  if (phase === 'checking') {
    return (
      <div className="h-screen w-screen relative overflow-hidden">
        <LightBackground />
        <div className="absolute right-[8%] top-[15%] bottom-[15%] z-10 w-[440px] max-w-[90vw] overflow-y-auto bg-white dark:bg-[#141e33]/90 dark:backdrop-blur-xl rounded-2xl shadow-[0_4px_30px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_40px_rgba(0,0,0,0.3)] dark:border dark:border-[#253554]/60 flex flex-col items-center px-10 py-8">
          <div className="my-auto flex flex-col items-center">
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <BoclawLogo
                size={56}
                orgLogoUrl={orgBranding?.logoUrl}
                orgCode={orgBranding?.code}
                orgName={orgBranding?.name}
              />
            </div>
            <div className="w-10 h-10 rounded-full border-[3px] border-gray-200 dark:border-gray-700 border-t-[#365EEC] animate-spin" />
            <p className="text-sm text-gray-400 mt-4">{i18nService.t('loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'redirecting') {
    return (
      <div className="h-screen w-screen relative overflow-hidden">
        <LightBackground />
        <style>{`
          @keyframes oa-circle-draw {
            0% { stroke-dashoffset: 220; opacity: 1; }
            100% { stroke-dashoffset: 0; opacity: 1; }
          }
          @keyframes oa-check-draw {
            0% { stroke-dashoffset: 55; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes oa-fade-up {
            0% { opacity: 0; transform: translateY(10px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .oa-circle {
            stroke-dasharray: 220;
            stroke-dashoffset: 220;
            opacity: 0;
            animation: oa-circle-draw 0.7s cubic-bezier(0.65, 0, 0.35, 1) 0.15s forwards;
          }
          .oa-check {
            stroke-dasharray: 55;
            stroke-dashoffset: 55;
            animation: oa-check-draw 0.45s cubic-bezier(0.65, 0, 0.35, 1) 0.75s forwards;
          }
          .oa-title { opacity: 0; animation: oa-fade-up 0.45s ease-out 1.2s forwards; }
          .oa-desc { opacity: 0; animation: oa-fade-up 0.45s ease-out 1.4s forwards; }
          .oa-footer { opacity: 0; animation: oa-fade-up 0.4s ease-out 1.6s forwards; }
        `}</style>
        <div className="absolute right-[8%] top-[15%] bottom-[15%] z-10 w-[440px] max-w-[90vw] overflow-y-auto bg-white dark:bg-[#141e33]/90 dark:backdrop-blur-xl rounded-2xl shadow-[0_4px_30px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_40px_rgba(0,0,0,0.3)] dark:border dark:border-[#253554]/60 flex flex-col items-center px-10 py-8">
          <div className="my-auto flex flex-col items-center">
            {/* Logo */}
            <div className="flex justify-center mb-[60px]">
              <BoclawLogo
                size={56}
                orgLogoUrl={orgBranding?.logoUrl}
                orgCode={orgBranding?.code}
                orgName={orgBranding?.name}
              />
            </div>
            {/* Animated circle + check drawn together */}
            <div className="mb-[60px]">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                {/* Circle outline draws, no fill — transparent inside */}
                <circle className="oa-circle" cx="40" cy="40" r="35" stroke="#22c55e" strokeWidth="3" fill="none" transform="rotate(-90 40 40)" />
                {/* Checkmark — proper proportions */}
                <path className="oa-check" d="M23 42l12 12L57 28" stroke="#22c55e" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </div>
            <h2 className="oa-title text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">
              {i18nService.t('oauthRedirecting')}
            </h2>
            <div className="oa-desc text-sm text-gray-400 text-center mb-8 space-y-1">
              {i18nService.t('oauthSuccessDesc').replace('{orgCode}', orgCode).split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          </div>
          <div className="mt-auto pt-4 text-center">
            <p className="oa-footer text-xs text-gray-400">
              Copyright &copy; {new Date().getFullYear()} {orgCode}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="h-screen w-screen relative overflow-hidden">
        <LightBackground />
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
              <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen relative overflow-hidden">
      <LightBackground />

      {/* Login panel — same layout as LoginPage: floating right */}
      <div className="absolute right-[8%] top-[15%] bottom-[15%] z-10 w-[440px] max-w-[90vw] overflow-y-auto bg-white dark:bg-[#141e33]/90 dark:backdrop-blur-xl rounded-2xl shadow-[0_4px_30px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_40px_rgba(0,0,0,0.3)] dark:border dark:border-[#253554]/60 flex flex-col px-10 py-8">
        <div className="my-auto">
        {/* Logo */}
        <div className="flex justify-center mb-[60px]">
          <BoclawLogo
            size={56}
            orgLogoUrl={orgBranding?.logoUrl}
            orgCode={orgBranding?.code}
            orgName={orgBranding?.name}
          />
        </div>

        {/* Title & subtitle */}
        <h2 className="text-xl font-bold text-center text-gray-800 dark:text-gray-100">
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm text-gray-400 text-center mt-1.5 mb-[60px]">
            {subtitle}
          </p>
        )}
        {!subtitle && <div className="mb-[60px]" />}

        <div className="space-y-5" onKeyDown={handleKeyDown}>
          {phase === 'authorize' && (
            <button
              type="button"
              onClick={() => { void handleAuthorize(); }}
              className="w-full rounded-lg bg-[#3b82f6] hover:bg-[#2563eb] text-white py-3 text-sm font-semibold tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? i18nService.t('saving') : i18nService.t('oauthAuthorizeButton')}
            </button>
          )}

          {phase === 'login' && !pending2FA && (
            <>
              {/* Username */}
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1.5">
                  {i18nService.t('authUsernamePlaceholder')}
                </label>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={i18nService.t('authUsernamePlaceholder')}
                  className="w-full rounded-lg border border-gray-200 dark:border-[#2a3f66]/50 dark:bg-[#1a2744]/60 bg-gray-50/80 px-4 py-3 text-sm text-gray-800 dark:text-gray-200 dark:placeholder-gray-500 outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-[#1e2f4d]/80 transition-all"
                  autoFocus
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1.5">
                  {i18nService.t('enterPassword')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={i18nService.t('enterPassword')}
                  className="w-full rounded-lg border border-gray-200 dark:border-[#2a3f66]/50 dark:bg-[#1a2744]/60 bg-gray-50/80 px-4 py-3 text-sm text-gray-800 dark:text-gray-200 dark:placeholder-gray-500 outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-[#1e2f4d]/80 transition-all"
                />
              </div>

              {/* Captcha */}
              {captchaData && (
                <div className="flex items-center gap-3">
                  <input
                    value={captchaCode}
                    onChange={(e) => setCaptchaCode(e.target.value)}
                    placeholder={i18nService.t('authVerificationCode')}
                    className="min-w-0 flex-1 rounded-lg border border-gray-200 dark:border-[#2a3f66]/50 dark:bg-[#1a2744]/60 bg-gray-50/80 px-4 py-3 text-sm text-gray-800 dark:text-gray-200 dark:placeholder-gray-500 outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-[#1e2f4d]/80 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => void loadCaptcha()}
                    className="shrink-0 w-[110px] rounded-lg overflow-hidden border border-gray-200 dark:border-[#2a3f66]/50 cursor-pointer hover:opacity-80 transition-opacity h-[46px]"
                    title={i18nService.t('authVerificationCode')}
                  >
                    <img src={captchaData} alt="captcha" className="h-full w-full object-contain" />
                  </button>
                </div>
              )}

              {/* Login button */}
              <button
                type="button"
                onClick={() => { void handleLoginSubmit(); }}
                className="w-full rounded-lg bg-[#3b82f6] hover:bg-[#2563eb] text-white py-3 text-sm font-semibold tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? i18nService.t('saving') : i18nService.t('oauthLoginAndAuthorize')}
              </button>
            </>
          )}

          {phase === 'login' && pending2FA && (
            <div className="space-y-3">
              <input
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                placeholder={i18nService.t('authTwoFactorCodePlaceholder')}
                className="w-full rounded-lg border border-gray-200 dark:border-[#2a3f66]/50 dark:bg-[#1a2744]/60 bg-gray-50/80 px-4 py-3 text-sm text-gray-800 dark:text-gray-200 dark:placeholder-gray-500 outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-[#1e2f4d]/80 transition-all"
                autoFocus
              />
              <button
                type="button"
                onClick={() => { setPending2FA(null); setTwoFactorCode(''); setError(null); }}
                className="text-xs text-blue-500 hover:text-blue-400 transition-colors"
              >
                {i18nService.t('authBackToLogin')}
              </button>

              <button
                type="button"
                onClick={() => { void handleLoginSubmit(); }}
                className="w-full rounded-lg bg-[#3b82f6] hover:bg-[#2563eb] text-white py-3 text-sm font-semibold tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? i18nService.t('saving') : i18nService.t('oauthVerifyAndAuthorize')}
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}
        </div>
        </div>

        {/* Footer inside panel */}
        <div className="mt-auto pt-4 text-center space-y-1.5">
          <p className="text-xs text-gray-400">
            {i18nService.t('oauthClientHint').replace('{clientId}', `${orgCode}-desktop`)}
          </p>
          <span className="text-xs text-gray-400">
            Copyright &copy; {new Date().getFullYear()} {orgCode}. All rights reserved.
          </span>
        </div>
      </div>
    </div>
  );
};

export default OAuthAuthorize;
